const DEFAULT_STORAGE_KEY = "aiRecording:leftSidebarState";

/**
 * Host `acv:global:leftSidebarState` lives on the ACV origin.
 * The iframe cannot read it. ACV seeds this key via URL `sb=c|e` (index.html)
 * and later via `acv-layout-info.leftSidebarCollapsed`.
 *
 * URL `sb` is applied once per document load, then localStorage is the live source
 * so a later read() does not overwrite an in-session user toggle.
 */
export function persistAiRecordingSidebarState(
  collapsed: boolean,
  storageKey = DEFAULT_STORAGE_KEY,
) {
  try {
    localStorage.setItem(storageKey, collapsed ? "collapsed" : "expanded");
  } catch {
    /* ignore */
  }
  try {
    (window as Window & { __acvPendingLeftSidebarCollapsed?: boolean }).__acvPendingLeftSidebarCollapsed =
      collapsed;
  } catch {
    /* ignore */
  }
}

export function applyAiRecordingSidebarWidth(collapsed: boolean) {
  try {
    const isNarrow =
      typeof document !== "undefined" &&
      document.documentElement.getAttribute("data-narrow-layout") === "1";
    const w = isNarrow || collapsed ? "64px" : "314px";
    document.documentElement.style.setProperty("--acv-sidebar-width", w);
    document.documentElement.style.setProperty("--acv-iframe-leftsidebar-width", w);
  } catch {
    /* ignore */
  }
}

function readSbParam(): string {
  try {
    return (new URLSearchParams(window.location.search).get("sb") || "").toLowerCase();
  } catch {
    return "";
  }
}

let hostSbApplied = false;

export function readAiRecordingSidebarCollapsed(
  storageKey = DEFAULT_STORAGE_KEY,
): boolean {
  if (typeof window === "undefined") return false;
  try {
    const pending = (
      window as Window & { __acvPendingLeftSidebarCollapsed?: boolean }
    ).__acvPendingLeftSidebarCollapsed;
    if (typeof pending === "boolean") {
      persistAiRecordingSidebarState(pending, storageKey);
      return pending;
    }
    if (!hostSbApplied) {
      const sb = readSbParam();
      if (sb === "c" || sb === "collapsed" || sb === "1") {
        hostSbApplied = true;
        persistAiRecordingSidebarState(true, storageKey);
        return true;
      }
      if (sb === "e" || sb === "expanded" || sb === "0") {
        hostSbApplied = true;
        persistAiRecordingSidebarState(false, storageKey);
        return false;
      }
    }
    const saved = localStorage.getItem(storageKey);
    if (saved === "collapsed" || saved === "expanded") {
      return saved === "collapsed";
    }
  } catch {
    /* ignore */
  }
  return false;
}

export const AIRECORDING_SIDEBAR_STORAGE_KEY = DEFAULT_STORAGE_KEY;
